import { DefinitionTooltip, Tooltip } from '@carbon/react';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import styles from './tooltip-icon.module.scss';

type TooltipAlign = ComponentProps<typeof Tooltip>['align'];
type DefinitionAlign = ComponentProps<typeof DefinitionTooltip>['align'];

// A text definition can name the button; a rich one needs a label to.
type TooltipIconText =
  | { definition?: string; label?: string }
  | { definition: ReactNode; label: string };

type TooltipIconProps = TooltipIconText & {
  children: ReactNode;
  /** Open on click or tap as well as hover and keyboard focus. */
  openOnClick?: boolean;
  direction?: string;
  align?: string;
  autoAlign?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  [key: string]: unknown;
};

/**
 * An icon button that explains itself in a tooltip. A compatibility wrapper for
 * the removed TooltipIcon component.
 * @param {TooltipIconProps} props The icon.
 * @return {JSX.Element} The icon.
 */
export function TooltipIcon({
  children,
  definition,
  label,
  openOnClick = false,
  direction,
  align,
  autoAlign,
  className,
  onClick,
  ...rest
}: TooltipIconProps) {
  const tooltipAlign = align || (direction === 'bottom' ? 'bottom' : 'top');

  // Carbon's Tooltip ignores focus that comes from a pointer, so a click or a
  // tap never opens it; its DefinitionTooltip toggles on either.
  if (openOnClick) {
    return (
      <DefinitionTooltip
        definition={definition}
        align={tooltipAlign as DefinitionAlign}
        autoAlign={autoAlign}
        openOnHover
        triggerClassName={classNames(styles.trigger, className)}
        aria-label={label}
        {...rest}
      >
        {children}
      </DefinitionTooltip>
    );
  }

  return (
    <Tooltip
      {...(label ? { description: definition } : { label: definition })}
      align={tooltipAlign as TooltipAlign}
      autoAlign={autoAlign}
    >
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-label={
          label ?? (typeof definition === 'string' ? definition : undefined)
        }
        style={{
          background: 'none',
          border: 'none',
          cursor: onClick ? 'pointer' : 'default',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
        }}
        {...rest}
      >
        {children}
      </button>
    </Tooltip>
  );
}
